// ── src/pages/SetupHotelPage.js ────────────────────────────────────
import React, { useState, useEffect } from "react";
import "./cx.css"; // theme tokens (--accent ...)
import "./SetupHotelPage.css";
import axios from "../api/axiosInstance";
import { Link, useNavigate } from "react-router-dom";
import { FiClock, FiZap } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import notify from "../utils/notify";

const SetupHotelPage = () => {
  // ⬇︎ pull in the merge‑helper, not the raw setter
  const { authData, updateAuthData } = useAuth();
  const [hotelId, setHotelId]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const navigate                = useNavigate();

  /* ── trial status ─────────────────────────────────────────────── */
  const trialTime  = authData?.trialTime;         // undefined → never started
  const notStarted = trialTime == null;
  const expired    = trialTime != null && trialTime <= 0;

  /* ── redirect if trial already active & KYC done ─────────────── */
  useEffect(() => {
    if (authData?.isKycCompleted && trialTime > 0) {
      navigate("/dashboard");
    }
    if (authData?.hotelId) {
      setHotelId(authData.hotelId);
    }
  }, [authData, trialTime, navigate]);

  /* ── activate / renew handler ─────────────────────────────────── */
  const handleRequest = async (e, mode) => {
    e.preventDefault();
    if (!hotelId) return notify.error("No hotel associated with your account.");

    setLoading(true);
    try {
      const endpoint  = mode === "activate" ? "/trial/activate" : "/trial/renew";
      const { data }  = await axios.post(
        endpoint,
        { hotelId },
        { headers: { Authorization: `Bearer ${authData.token}` } }
      );

      /* server returns { token, remainingTrialDays, … } */
      updateAuthData({
        token: data.token,
        trialTime: data.remainingTrialDays
      });

      notify.success(
        mode === "activate"
          ? "Trial activated successfully!"
          : "Trial renewed successfully!"
      );
      navigate("/dashboard");
    } catch (err) {
      console.error(`Error on ${mode}:`, err.response ?? err);
      notify.error(
        `Failed to ${
          mode === "activate" ? "activate" : "renew"
        } trial. Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  /* ── ui ───────────────────────────────────────────────────────── */
  const starting = notStarted || expired;

  return (
    <div className="setup-wrap">
      <div className="setup-card">
        <span className="setup-icon" aria-hidden="true">
          {expired ? <FiClock /> : <FiZap />}
        </span>

        {starting ? (
          <>
            <h1 className="setup-title">{notStarted ? "Start your 30-day free trial" : "Your free trial has ended"}</h1>
            <p className="setup-text">
              {notStarted
                ? "Activate your trial to start using Nnetego HMS for your property. No payment details needed."
                : "Start a new 30-day period to keep using Nnetego HMS."}
            </p>

            {authData?.hotelName && <span className="setup-property">{authData.hotelName}</span>}

            <form onSubmit={(e) => handleRequest(e, notStarted ? "activate" : "renew")}>
              <button type="submit" className="setup-btn" disabled={!hotelId || loading}>
                {loading ? (notStarted ? "Activating..." : "Starting...") : notStarted ? "Activate free trial" : "Start new 30-day trial"}
              </button>
            </form>

            <p className="setup-help">
              Need help? <Link to="/support">Contact support</Link>
            </p>
          </>
        ) : (
          <>
            <h1 className="setup-title">Getting things ready</h1>
            <div className="setup-skeleton" role="status" aria-label="Loading" />
          </>
        )}
      </div>
    </div>
  );
};

export default SetupHotelPage;
